import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Observable, Subject, filter } from 'rxjs';

export enum AlertType {
  Success,
  Error,
  Info,
  Warning
}

export interface Alert {
  id: number;
  type: AlertType;
  message: string;
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {

  private subject = new Subject<Alert | null>();

  private keepAfterRouteChange = false;
  private alertId = 0;

  constructor(private router: Router) {

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart)
      )
      .subscribe(() => {

        if (this.keepAfterRouteChange) {
          this.keepAfterRouteChange = false;
        } else {
          this.clear();
        }

      });
  }

  // Get alert observable
  getAlert(): Observable<Alert | null> {
    return this.subject.asObservable();
  }

  // Success notification
  success(
    message: string,
    keepAfterRouteChange = false,
    duration = 5000
  ): void {
    this.alert(
      AlertType.Success,
      message,
      keepAfterRouteChange,
      duration
    );
  }

  // Error notification
  error(
    message: string,
    keepAfterRouteChange = false,
    duration = 5000
  ): void {
    this.alert(
      AlertType.Error,
      message,
      keepAfterRouteChange,
      duration
    );
  }

  // Information notification
  info(
    message: string,
    keepAfterRouteChange = false,
    duration = 5000
  ): void {
    this.alert(
      AlertType.Info,
      message,
      keepAfterRouteChange,
      duration
    );
  }

  // Warning notification
  warn(
    message: string,
    keepAfterRouteChange = false,
    duration = 5000
  ): void {
    this.alert(
      AlertType.Warning,
      message,
      keepAfterRouteChange,
      duration
    );
  }

  // Common alert method
  alert(
    type: AlertType,
    message: string,
    keepAfterRouteChange = false,
    duration = 5000
  ): void {

    if (!message) {
      return;
    }

    this.keepAfterRouteChange = keepAfterRouteChange;

    const alert: Alert = {
      id: ++this.alertId,
      type,
      message,
      duration
    };

    this.subject.next(alert);
  }

  // Clear notifications
  clear(): void {
    this.keepAfterRouteChange = false;
    this.subject.next(null);
  }
}